import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { InviteUsersModal } from '@/components/InviteUsersModal'
import { useInvites, useSendInvites } from '@/hooks/queries/credit/useInvites'
import { useRoles } from '@/hooks/queries/credit/useRoles'
import { invitesSearchSchema } from '@/hooks/queries/useInvites'
import { useTablePagination } from '@/hooks/useTablePagination'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { UserRoundPlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import InvitesDataTable from './-components/InvitesDataTable'

export const Route = createFileRoute('/_auth/_modules/credit/admin/invitations/')({
  component: InvitationsPage,
  validateSearch: (search) => invitesSearchSchema.parse(search),
})

function InvitationsPage() {
  const { t } = useTranslation('admin')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const {
    isPending: isInvitesPending,
    data: { invites, count } = { invites: [], count: 0 },
    error: invitesError,
  } = useInvites({ ...search, page: search.page ?? 1, pageSize })
  const { isPending: isRolesPending, data: roles, error: rolesError } = useRoles()

  return (
    <PageContainer>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.INVITATIONS}
        title={t('tutorials.dialog.invitations.title')}
        guideUrl="/admin/invitations"
      >
        {t('tutorials.dialog.invitations.description')}
      </IntroductionDialog>

      <Spread>
        <PageHeader>{t('invitationsPage.header.heading')}</PageHeader>
        <PermissionsGuard required={['credit_invites_send']}>
          <InviteUsersModal
            roles={roles}
            useSendInvitesHook={useSendInvites}
            trigger={
              <Button type="button">
                <UserRoundPlusIcon /> {t('invitationsPage.header.action')}
              </Button>
            }
          />
        </PermissionsGuard>
      </Spread>

      <Card>
        <CardHeader>
          <Spread>
            <CardTitle>{t('invitationsPage.title')}</CardTitle>
          </Spread>
        </CardHeader>

        <CardContent>
          <InvitesDataTable
            invites={invites}
            roles={roles}
            count={count}
            isPending={isInvitesPending || isRolesPending}
            error={invitesError || rolesError}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
